/**
 * <s-product-card> — Collection card controller.
 *
 * Commands (via commandfor/command on descendant buttons):
 *   --show-image — Scroll the card gallery to the slide at the source's data-index.
 *
 * Reads:
 *   [data-card-variants]   — JSON array of the product's variants.
 *   [data-card-option]     — Option inputs/selects with data-option-index.
 *   [data-slideshow-track] / [data-slide] — Gallery slides (data-media-id on each).
 *   [data-card-thumb]      — Thumbnail buttons with data-index.
 *   input[name="id"]       — Submitted variant ID.
 *   button[name="add"]     — Add-to-cart button, disabled when the variant is unavailable.
 */
class SProductCard extends HTMLElement {
  #ac;
  #variants = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    try {
      this.#variants = JSON.parse(this.querySelector('[data-card-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }

    this.addEventListener('command', (event) => {
      if (event.command !== '--show-image') return;
      const index = Number.parseInt(event.source?.dataset.index || '0', 10);
      this.#showSlide(index);
    }, opts);

    this.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-card-option]')) this.#updateVariant();
    }, opts);

    this.addEventListener('slideshow:change', (event) => {
      this.#markThumb(event.detail?.index ?? 0);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #slides() {
    return Array.from(this.querySelectorAll('[data-slide]'));
  }

  #showSlide(index) {
    const track = this.querySelector('[data-slideshow-track]');
    const slide = this.#slides()[index];
    if (!track || !slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#markThumb(index);
  }

  #markThumb(index) {
    this.querySelectorAll('[data-card-thumb]').forEach((thumb) => {
      const active = Number.parseInt(thumb.dataset.index || '-1', 10) === index;
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  #updateVariant() {
    const selected = [];
    this.querySelectorAll('[data-card-option]').forEach((input) => {
      if (input instanceof HTMLInputElement && !input.checked) return;
      selected[Number.parseInt(input.dataset.optionIndex || '0', 10)] = input.value;
    });

    const variant = this.#variants.find((v) => v.options.every((value, i) => selected[i] === undefined || selected[i] === value));
    if (!variant) return;

    const idInput = this.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variant.id);

    const addButton = this.querySelector('button[name="add"]');
    if (addButton) {
      addButton.disabled = !variant.available;
      addButton.setAttribute('aria-disabled', String(!variant.available));
      addButton.classList.toggle('opacity-50', !variant.available);
    }

    const mediaId = variant.featured_media?.id;
    if (mediaId) {
      const index = this.#slides().findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.#showSlide(index);
    }
  }
}

if (!customElements.get('s-product-card')) customElements.define('s-product-card', SProductCard);
